import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Upload, Download, Shield, Zap, Clock, Users, Lock, Globe, 
  Smartphone, CheckCircle, ArrowRight, Loader2, AlertCircle,
  Palette, Video, Megaphone, Building, FileArchive, Mail, Star, Heart,
  ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  Upload, Download, Shield, Zap, Clock, Users, Lock, Globe,
  Smartphone, CheckCircle, ArrowRight, Palette, Video, Megaphone,
  Building, FileArchive, Mail, Star, Heart
};

// Color classes
const getColorClasses = (color) => {
  const colors = {
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    green: 'bg-green-500/20 text-green-400 border-green-500/30',
    yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    red: 'bg-red-500/20 text-red-400 border-red-500/30',
    orange: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    pink: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
    indigo: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    emerald: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
  };
  return colors[color] || colors.blue;
};

const DynamicPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPage();
  }, [slug]);

  const loadPage = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/pages/${slug}`);
      setPage(res.data);
    } catch (err) {
      console.error('Error loading page:', err);
      setError(err.response?.status === 404 ? 'Page non trouvée' : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
        <NavigationMenu />
        <main className="pt-24 pb-16">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">{error || 'Page non trouvée'}</h1>
            <p className="text-blue-300/70 mb-6">La page demandée n'existe pas ou n'est pas disponible.</p>
            <Link to="/">
              <Button className="bg-blue-600 hover:bg-blue-700">Retour à l'accueil</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <NavigationMenu />
      
      <main className="pt-24 pb-16">
        {page.sections?.map((section, index) => (
          <RenderSection key={section.id || index} section={section} />
        ))}
        
        {(!page.sections || page.sections.length === 0) && (
          <div className="max-w-4xl mx-auto px-6 text-center py-20">
            <h1 className="text-4xl font-bold text-white mb-4">{page.title}</h1>
            {page.meta_description && (
              <p className="text-blue-200/70 text-lg">{page.meta_description}</p>
            )}
          </div>
        )}
      </main>

      <DynamicFooter fixed={true} />
    </div>
  );
};

// Render different section types
const RenderSection = ({ section }) => {
  switch (section.type) {
    case 'hero':
      return <HeroSection section={section} />;
    case 'features_grid':
      return <FeaturesGridSection section={section} />;
    case 'use_cases':
      return <UseCasesSection section={section} />;
    case 'pricing_plans':
      return <PricingSection section={section} />;
    case 'text':
      return <TextSection section={section} />;
    case 'content':
      return <ContentSection section={section} />;
    case 'faq':
      return <FAQSection section={section} />;
    case 'timeline':
      return <TimelineSection section={section} />;
    default:
      return null;
  }
};

// Hero Section
const HeroSection = ({ section }) => (
  <section className="max-w-6xl mx-auto px-6 text-center mb-20">
    <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-300 text-sm mb-6">
      <Zap className="w-4 h-4" />
      {section.badge || 'Découvrez nos fonctionnalités'}
    </div>
    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
      {section.title}<br />
      {section.subtitle && (
        <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
          {section.subtitle}
        </span>
      )}
    </h1>
    {section.description && (
      <p className="text-lg md:text-xl text-blue-200/70 max-w-3xl mx-auto">
        {section.description}
      </p>
    )}
    {section.cta_text && (
      <Link to={section.cta_link || '/'} className="inline-block mt-8">
        <Button size="lg" className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600">
          {section.cta_text}
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </Link>
    )}
  </section>
);

// Features Grid Section
const FeaturesGridSection = ({ section }) => (
  <section className="max-w-6xl mx-auto px-6 mb-20">
    {section.title && (
      <h2 className="text-3xl font-bold text-white text-center mb-10">{section.title}</h2>
    )}
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {section.items?.map((item, index) => {
        const Icon = iconMap[item.icon] || Star;
        return (
          <div 
            key={index}
            className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6 hover:bg-slate-800/70 hover:border-blue-500/30 transition-all group"
          >
            <div className={`w-14 h-14 rounded-xl ${getColorClasses(item.color)} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
              <Icon className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
            <p className="text-blue-200/60 leading-relaxed">{item.description}</p>
          </div>
        );
      })}
    </div>
  </section>
);

// Use Cases Section
const UseCasesSection = ({ section }) => (
  <section className="max-w-6xl mx-auto px-6 mb-20">
    {section.title && (
      <h2 className="text-3xl font-bold text-white text-center mb-10">{section.title}</h2>
    )}
    <div className="grid md:grid-cols-2 gap-6">
      {section.items?.map((item, index) => {
        const Icon = iconMap[item.icon] || Star;
        return (
          <div 
            key={index}
            className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6 hover:bg-slate-800/70 transition-all"
          >
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-500/20 rounded-xl">
                <Icon className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-blue-200/60">{item.description}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </section>
);

// Pricing Section
const PricingSection = ({ section }) => (
  <section className="max-w-5xl mx-auto px-6 mb-20">
    {section.title && (
      <h2 className="text-3xl font-bold text-white text-center mb-10">{section.title}</h2>
    )}
    <div className="grid md:grid-cols-2 gap-8">
      {section.plans?.map((plan, index) => (
        <div
          key={index}
          className={`relative rounded-3xl p-8 ${
            plan.popular
              ? 'bg-gradient-to-br from-blue-600/20 to-cyan-600/20 border-2 border-cyan-500/50'
              : 'bg-slate-800/50 border border-blue-500/20'
          }`}
        >
          {plan.popular && (
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-medium px-4 py-1 rounded-full">
                Populaire
              </span>
            </div>
          )}
          <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
          <p className="text-blue-300/70 mb-4">{plan.description}</p>
          <div className="flex items-baseline gap-1 mb-6">
            <span className="text-5xl font-bold text-white">{plan.price}</span>
            {plan.period && <span className="text-blue-300/70">{plan.period}</span>}
          </div>
          <ul className="space-y-3 mb-8">
            {plan.features?.map((feature, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                <span className="text-blue-100/80">{feature}</span>
              </li>
            ))}
          </ul>
          <Link to={plan.cta_link || '/pricing'}>
            <Button className={`w-full py-6 ${
              plan.popular
                ? 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600'
                : 'bg-slate-700 hover:bg-slate-600'
            }`}>
              {plan.cta}
            </Button>
          </Link>
        </div>
      ))}
    </div>
  </section>
);

// Text Section (Markdown-like)
const TextSection = ({ section }) => (
  <section className="max-w-4xl mx-auto px-6 mb-20">
    <div className="bg-slate-800/30 border border-blue-500/20 rounded-2xl p-8">
      {section.title && (
        <h2 className="text-2xl font-bold text-white mb-6">{section.title}</h2>
      )}
      <div className="prose prose-invert max-w-none">
        {section.content?.split('\n').map((paragraph, index) => {
          if (paragraph.startsWith('# ')) {
            return <h1 key={index} className="text-3xl font-bold text-white mb-4">{paragraph.slice(2)}</h1>;
          }
          if (paragraph.startsWith('## ')) {
            return <h2 key={index} className="text-2xl font-bold text-white mb-3">{paragraph.slice(3)}</h2>;
          }
          if (paragraph.startsWith('### ')) {
            return <h3 key={index} className="text-xl font-semibold text-white mb-2">{paragraph.slice(4)}</h3>;
          }
          if (paragraph.startsWith('- ')) {
            return <li key={index} className="text-blue-200/70 ml-4">{paragraph.slice(2)}</li>;
          }
          if (paragraph.trim()) {
            return <p key={index} className="text-blue-200/70 mb-4">{paragraph}</p>;
          }
          return null;
        })}
      </div>
    </div>
  </section>
);

// Content Section (for About, Legal pages)
const ContentSection = ({ section }) => (
  <section className="max-w-4xl mx-auto px-6 mb-12">
    {section.title && (
      <h2 className="text-2xl font-bold text-white mb-6">{section.title}</h2>
    )}
    <div className="bg-slate-800/30 border border-blue-500/10 rounded-2xl p-6 md:p-8">
      <div className="prose prose-invert max-w-none">
        {section.content?.split('\n').map((paragraph, index) => {
          const trimmed = paragraph.trim();
          if (!trimmed) return <br key={index} />;
          
          // Headers
          if (trimmed.startsWith('### ')) {
            return <h3 key={index} className="text-lg font-semibold text-white mt-6 mb-3">{trimmed.slice(4)}</h3>;
          }
          if (trimmed.startsWith('## ')) {
            return <h2 key={index} className="text-xl font-bold text-white mt-6 mb-3">{trimmed.slice(3)}</h2>;
          }
          if (trimmed.startsWith('# ')) {
            return <h1 key={index} className="text-2xl font-bold text-white mt-6 mb-4">{trimmed.slice(2)}</h1>;
          }
          
          // Bold text with **
          if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
            return <p key={index} className="text-white font-semibold mb-2">{trimmed.slice(2, -2)}</p>;
          }
          
          // List items
          if (trimmed.startsWith('- ')) {
            return <li key={index} className="text-blue-200/70 ml-4 mb-1">{trimmed.slice(2)}</li>;
          }
          
          // Regular paragraphs - handle inline **bold**
          const parts = trimmed.split(/(\*\*[^*]+\*\*)/g);
          return (
            <p key={index} className="text-blue-200/70 mb-3 leading-relaxed">
              {parts.map((part, i) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                  return <strong key={i} className="text-white font-medium">{part.slice(2, -2)}</strong>;
                }
                return part;
              })}
            </p>
          );
        })}
      </div>
    </div>
  </section>
);

// FAQ Section
const FAQSection = ({ section }) => (
  <section className="max-w-4xl mx-auto px-6 mb-12">
    {section.title && (
      <h2 className="text-2xl font-bold text-white mb-10">{section.title}</h2>
    )}
    <div className="space-y-4">
      {section.items?.map((item, index) => (
        <details 
          key={index} 
          className="group bg-slate-800/40 border border-blue-500/15 rounded-xl overflow-hidden"
        >
          <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-slate-800/60 transition-colors list-none [&::-webkit-details-marker]:hidden">
            <h3 className="text-white font-medium pr-4 text-left">{item.question}</h3>
            <ChevronDown className="w-5 h-5 text-white flex-shrink-0 transition-transform group-open:rotate-180" />
          </summary>
          <div className="px-5 pb-5 pt-0">
            <p className="text-blue-200/70 leading-relaxed text-left">{item.answer}</p>
          </div>
        </details>
      ))}
    </div>
  </section>
);

// Timeline Section
const TimelineSection = ({ section }) => (
  <section className="max-w-4xl mx-auto px-6 mb-16">
    {section.title && (
      <h2 className="text-2xl font-bold text-white text-center mb-12">{section.title}</h2>
    )}
    <div className="relative">
      {/* Vertical line */}
      <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-cyan-500 via-blue-500 to-cyan-500/30 transform md:-translate-x-1/2"></div>
      
      <div className="space-y-8">
        {section.items?.map((item, index) => {
          const isEven = index % 2 === 0;
          return (
            <div 
              key={index}
              className={`relative flex items-start gap-6 ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`}
            >
              {/* Timeline dot */}
              <div className="absolute left-4 md:left-1/2 w-4 h-4 bg-cyan-500 rounded-full border-4 border-slate-900 transform -translate-x-1/2 z-10 shadow-lg shadow-cyan-500/50"></div>
              
              {/* Content card */}
              <div className={`ml-12 md:ml-0 md:w-[calc(50%-2rem)] ${isEven ? 'md:pr-8 md:text-right' : 'md:pl-8 md:text-left'}`}>
                <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-5 hover:border-cyan-500/40 transition-all">
                  <div className={`flex items-center gap-3 mb-2 ${isEven ? 'md:justify-end' : ''}`}>
                    <span className="text-cyan-400 font-bold text-lg">{item.year}</span>
                  </div>
                  <h3 className="text-white font-semibold text-base mb-2">{item.title}</h3>
                  <p className="text-blue-200/60 text-sm leading-relaxed mb-3">{item.description}</p>
                  {item.image && (
                    <div className={`mt-4 ${isEven ? 'md:flex md:justify-end' : ''}`}>
                      <img 
                        src={item.image} 
                        alt={`SENDBOX ${item.year}`}
                        className="rounded-lg border border-blue-500/20 shadow-lg max-w-full md:max-w-[280px] hover:border-cyan-500/50 transition-all"
                      />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Spacer for alternating layout */}
              <div className="hidden md:block md:w-[calc(50%-2rem)]"></div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

export default DynamicPage;
